import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Public "Acknowledge / request reassignment" page reached from the ticket
 * assignment email link:
 *   /ticket-action?t=<token>
 * No login required — the signed token authorises exactly one actor
 * (the assignee, or someone at a specific level in the ticket's escalation
 * chain) to acknowledge, request reassignment, or reassign.
 */
export default function TicketAction() {
  const token = new URLSearchParams(window.location.search).get("t") || "";

  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [mode, setMode] = useState(null); // null | "reassign_note" | "pick_tech"
  const [note, setNote] = useState("");
  const [techId, setTechId] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setState({ loading: false, error: "This link is missing its code.", data: null }); return; }
    api.ticketActionInfo(token)
      .then((r) => setState({ loading: false, error: "", data: r }))
      .catch((e) => setState({ loading: false, error: e.message || "This link is invalid or has expired.", data: null }));
  }, [token]);

  const act = async (action, payload) => {
    setSaving(true);
    try {
      await api.ticketActionDo(token, action, payload);
      setDone(action);
    } catch (e) {
      setState((s) => ({ ...s, error: e.message || "Could not complete that action." }));
    } finally {
      setSaving(false);
    }
  };

  const wrap = { minHeight: "100vh", display: "grid", placeItems: "center", background: "#f6f8fc", padding: 24, fontFamily: "Inter, system-ui, sans-serif" };
  const card = { width: "100%", maxWidth: 480, background: "#fff", borderRadius: 16, padding: 32, boxShadow: "0 10px 40px rgba(13,26,58,.08)", border: "1px solid #eef1f7" };

  if (state.loading) return <div style={wrap}><div style={card}>Loading…</div></div>;
  if (state.error && !state.data) return (
    <div style={wrap}><div style={card}>
      <h2 style={{ margin: 0, color: "#0D1A3A" }}>Link unavailable</h2>
      <p style={{ color: "#5b6b8c" }}>{state.error}</p>
    </div></div>
  );

  if (done) {
    const messages = {
      acknowledge: "Ticket acknowledged — thanks for confirming.",
      request_reassign: "Reassignment request sent up the escalation chain.",
      reassign: "Ticket reassigned to the selected technician.",
    };
    return (
      <div style={wrap}><div style={{ ...card, textAlign: "center" }}>
        <div style={{ fontSize: 40 }}>✓</div>
        <h2 style={{ margin: "8px 0", color: "#0D1A3A" }}>Done</h2>
        <p style={{ color: "#5b6b8c" }}>{messages[done] || "Action completed."}</p>
      </div></div>
    );
  }

  const { ticket, role, level, actor, technicians = [] } = state.data;
  const canManage = role === "chain";

  return (
    <div style={wrap}><div style={card}>
      <p style={{ color: "#0654FC", fontWeight: 600, margin: 0, fontSize: 13 }}>
        {ticket.ticket_no} · {ticket.category} · {ticket.priority}
      </p>
      <h2 style={{ margin: "4px 0 2px", color: "#0D1A3A" }}>{ticket.title}</h2>
      {ticket.description && <p style={{ color: "#5b6b8c", marginTop: 0 }}>{ticket.description}</p>}
      {ticket.requester && <p style={{ color: "#96a1b8", fontSize: 12 }}>Raised by {ticket.requester}</p>}

      {state.error && <p style={{ color: "#c0392b", fontSize: 13 }}>{state.error}</p>}

      <p style={{ marginTop: 16, marginBottom: 10, color: "#243b5e" }}>
        Hi {actor.name}{canManage ? ` (escalation level ${level})` : ""}, what would you like to do?
      </p>

      {!mode && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button disabled={saving} onClick={() => act("acknowledge")}
            style={{ padding: "12px 18px", borderRadius: 10, border: "none", background: "#1BA85C", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
            {canManage ? "Acknowledge & start work" : "Acknowledge"}
          </button>
          {role === "assignee" && (
            <button disabled={saving} onClick={() => setMode("reassign_note")}
              style={{ padding: "12px 18px", borderRadius: 10, border: "none", background: "#c0392b", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              Request reassignment
            </button>
          )}
          {canManage && (
            <button disabled={saving} onClick={() => setMode("pick_tech")}
              style={{ padding: "12px 18px", borderRadius: 10, border: "none", background: "#0654FC", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              Reassign to…
            </button>
          )}
        </div>
      )}

      {mode === "reassign_note" && (
        <div style={{ marginTop: 14 }}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Reason (optional) — e.g. on leave, overloaded"
            style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, border: "1px solid #dce1ec", padding: 12, resize: "vertical", fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button disabled={saving} onClick={() => act("request_reassign", { note })}
              style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#c0392b", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              {saving ? "…" : "Send request"}
            </button>
            <button disabled={saving} onClick={() => setMode(null)}
              style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #dce1ec", background: "#fff", color: "#5b6b8c", cursor: "pointer" }}>
              Back
            </button>
          </div>
        </div>
      )}

      {mode === "pick_tech" && (
        <div style={{ marginTop: 14 }}>
          <select value={techId} onChange={(e) => setTechId(e.target.value)}
            style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10, border: "1px solid #dce1ec" }}>
            <option value="">Choose technician…</option>
            {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button disabled={saving || !techId} onClick={() => act("reassign", { technician_id: techId })}
              style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#0654FC", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              {saving ? "…" : "Reassign"}
            </button>
            <button disabled={saving} onClick={() => setMode(null)}
              style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #dce1ec", background: "#fff", color: "#5b6b8c", cursor: "pointer" }}>
              Back
            </button>
          </div>
        </div>
      )}
    </div></div>
  );
}
